/*global kakao*/
import React, { useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ReactMoment from "react-moment";

// 서버(WalletGruSavingWrite)와 동일한 허용 반경
export const ALLOW_DISTANCE = 150;

/** 두 좌표 사이 거리(m). 서버와 같은 Haversine 식 */
export const getDistance = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** 좌표 문자열이 실제 숫자인지 */
export const hasCoords = (lng, lat) =>
  !!lng && !!lat && !isNaN(parseFloat(lng)) && !isNaN(parseFloat(lat));

/** 34m / 1.4km 형태로 표기 */
export const distanceText = (meters) =>
  meters >= 1000
    ? `${(meters / 1000).toFixed(1)}km`
    : `${Math.round(meters)}m`;

const MAP_ID = "gruSavingLocationMap";

export default ({ isOpen, toggle, gruSaving }) => {
  const mapRef = useRef(null);

  const storeLng = gruSaving?.store?.longitude;
  const storeLat = gruSaving?.store?.latitude;
  const scanLng = gruSaving?.longitude;
  const scanLat = gruSaving?.latitude;

  const storeOk = hasCoords(storeLng, storeLat);
  const scanOk = hasCoords(scanLng, scanLat);

  const distance =
    storeOk && scanOk
      ? getDistance(
          parseFloat(storeLat),
          parseFloat(storeLng),
          parseFloat(scanLat),
          parseFloat(scanLng)
        )
      : null;
  const isOut = distance !== null && distance > ALLOW_DISTANCE;

  // 모달이 열린 뒤에야 지도를 담을 div 가 생기므로 onOpened 시점에 그린다.
  const drawMap = () => {
    if (typeof kakao === "undefined" || !kakao.maps) return;

    const container = document.getElementById(MAP_ID);
    if (!container) return;

    const storePos = storeOk
      ? new kakao.maps.LatLng(parseFloat(storeLat), parseFloat(storeLng))
      : null;
    const scanPos = scanOk
      ? new kakao.maps.LatLng(parseFloat(scanLat), parseFloat(scanLng))
      : null;

    const center = scanPos || storePos;
    if (!center) return;

    const map = new kakao.maps.Map(container, { center, level: 4 });
    mapRef.current = map;

    // 가맹점 — 파란 마커 + 허용 반경 원
    if (storePos) {
      new kakao.maps.Marker({
        position: storePos,
        map,
        title: gruSaving?.store?.name || "가맹점",
      });
      new kakao.maps.Circle({
        center: storePos,
        radius: ALLOW_DISTANCE,
        strokeWeight: 2,
        strokeColor: "#2D68FF",
        strokeOpacity: 0.9,
        strokeStyle: "dashed",
        fillColor: "#2D68FF",
        fillOpacity: 0.08,
        map,
      });
      new kakao.maps.CustomOverlay({
        position: storePos,
        yAnchor: 2.2,
        content:
          '<div style="padding:2px 8px;background:#fff;border:1px solid #dfe3ea;border-radius:10px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.1)">가맹점</div>',
        map,
      });
    }

    // 스캔 위치 — 반경 밖이면 빨강
    if (scanPos) {
      const color = isOut ? "#FF5473" : "#2E9E5B";
      new kakao.maps.CustomOverlay({
        position: scanPos,
        content: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>`,
        map,
      });
      new kakao.maps.CustomOverlay({
        position: scanPos,
        yAnchor: 2.2,
        content: `<div style="padding:2px 8px;background:#fff;border:1px solid #dfe3ea;border-radius:10px;font-size:11px;font-weight:700;white-space:nowrap;color:${color};box-shadow:0 1px 3px rgba(0,0,0,.1)">스캔 위치</div>`,
        map,
      });
    }

    // 두 지점을 잇는 선
    if (storePos && scanPos) {
      new kakao.maps.Polyline({
        path: [storePos, scanPos],
        strokeWeight: 3,
        strokeColor: "#999999",
        strokeOpacity: 0.9,
        strokeStyle: "shortdash",
        map,
      });

      // 두 지점과 반경 원이 모두 들어오도록 화면 범위를 맞춘다.
      const bounds = new kakao.maps.LatLngBounds();
      bounds.extend(storePos);
      bounds.extend(scanPos);
      map.setBounds(bounds, 40, 40, 40, 40);
    }
  };

  useEffect(() => {
    if (!isOpen) mapRef.current = null;
  }, [isOpen]);

  if (!gruSaving) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} onOpened={drawMap} size="lg">
      <ModalHeader toggle={toggle}>적립 위치 확인</ModalHeader>
      <ModalBody>
        {scanOk || storeOk ? (
          <>
            <div
              id={MAP_ID}
              style={{
                width: "100%",
                height: 320,
                border: "1px solid #e2e7eb",
                borderRadius: 4,
              }}
            ></div>

            <div className="f-s-12 text-muted m-t-10">
              <span className="m-r-15">
                <i
                  className="fa fa-circle m-r-5"
                  style={{ color: "#2D68FF" }}
                ></i>
                가맹점
              </span>
              <span className="m-r-15">
                <i
                  className="fa fa-circle m-r-5"
                  style={{ color: "#2E9E5B" }}
                ></i>
                스캔 위치(반경 안)
              </span>
              <span className="m-r-15">
                <i
                  className="fa fa-circle m-r-5"
                  style={{ color: "#FF5473" }}
                ></i>
                스캔 위치(반경 밖)
              </span>
              <span>점선 원 = 허용 반경 {ALLOW_DISTANCE}m</span>
            </div>
          </>
        ) : (
          <div className="alert alert-warning m-b-0">
            가맹점과 스캔 위치 좌표가 모두 없어 지도를 표시할 수 없습니다.
          </div>
        )}

        <table className="table table-bordered m-t-15 m-b-0">
          <tbody>
            <tr>
              <th style={{ width: 140 }}>가맹점</th>
              <td>
                {gruSaving.store?.name}
                <div className="f-s-12 text-muted">
                  {storeOk ? `${storeLat}, ${storeLng}` : "좌표 없음"}
                </div>
              </td>
            </tr>
            <tr>
              <th>스캔 위치</th>
              <td>
                {scanOk ? (
                  <span className="f-s-12 text-muted">{`${scanLat}, ${scanLng}`}</span>
                ) : gruSaving.gruSavingInfo?.isLocationCheck ? (
                  <>
                    <span className="label label-warning">위치 권한 비활성</span>
                    <div className="f-s-12 text-muted m-t-5">
                      앱이 좌표를 받지 못했습니다. 위치 권한 거부·위치 서비스
                      꺼짐·실내 미확보 중 하나이며, 적립은 그대로 처리됩니다.
                    </div>
                  </>
                ) : (
                  <span className="f-s-12 text-muted">
                    검증 미사용 — 좌표를 수집하지 않습니다.
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>거리</th>
              <td>
                {distance === null ? (
                  <span className="text-muted">-</span>
                ) : (
                  <>
                    <b>{distanceText(distance)}</b>{" "}
                    {isOut ? (
                      <span className="label label-danger m-l-5">반경 밖</span>
                    ) : (
                      <span className="label label-success m-l-5">반경 안</span>
                    )}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th>위치 검증</th>
              <td>
                {gruSaving.gruSavingInfo?.isLocationCheck ? (
                  "사용"
                ) : (
                  <span className="text-muted">미사용</span>
                )}
              </td>
            </tr>
            <tr>
              <th>적립일시</th>
              <td>
                <ReactMoment format="YYYY-MM-DD HH:mm:ss">
                  {gruSaving.createdAt}
                </ReactMoment>
              </td>
            </tr>
          </tbody>
        </table>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-white" onClick={toggle}>
          닫기
        </button>
      </ModalFooter>
    </Modal>
  );
};
